import type { ReactNode } from 'react';
import React from 'react';
import type { EventWithTarget } from 'corgi-types';

import Debounce from '../debounce';
import type { WithFirstTouchComponent } from './hoc/withFirstTouch';
import { withFirstTouch } from './hoc/withFirstTouch';

const Container = withFirstTouch<'div'>('div');

interface KeepFocusProps {
  /** Render prop that will be called with { focused } when components state changes */
  children: (props: { focused: boolean }) => ReactNode | ReactNode[];
  /** Should component keep track of focused state or not */
  active?: boolean;
  /** Should component ignore mouse hover events */
  disableHover?: boolean;
  /** Callback called when focus is lost (focused changes from true to false) */
  onFocusLost?: () => void;
}

interface KeepFocusState {
  focused: boolean;
}

enum EventType {
  Focus = 'focus',
}

export default class KeepFocusWithFirstTouch extends React.Component<
  KeepFocusProps,
  KeepFocusState
> {
  static defaultProps = {
    active: false,
  };

  debouncedFocusChange: Debounce;
  debouncedSetState: Debounce;
  container: null | WithFirstTouchComponent<'div'>;
  innerElem: null | HTMLElement;

  constructor(props: KeepFocusProps) {
    super(props);
    this.state = { focused: false };

    this.setFocused = this.setFocused.bind(this);
    this.handleFirstTouch = this.handleFirstTouch.bind(this);
    this.handleMouseChange = this.handleMouseChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.setContainerRef = this.setContainerRef.bind(this);
    this.setInnerRef = this.setInnerRef.bind(this);

    this.debouncedFocusChange = new Debounce(this.onFocusChange, 1000 / 60, this);
    this.debouncedSetState = new Debounce(this.setFocused, 150, this);

    this.container = null;
    this.innerElem = null;
  }

  setFocused(focused: boolean) {
    if (!focused && this.container && typeof this.container.resetFirstTouch === 'function') {
      this.container.resetFirstTouch();
    }

    // Call onFocusLost callback when focus changes from true to false
    if (!focused && this.state.focused && this.props.onFocusLost) {
      this.props.onFocusLost();
    }

    this.setState(prevState => (prevState.focused !== focused ? { focused } : null));
  }

  handleFirstTouch(event: TouchEvent) {
    if (!this.props.active) {
      return;
    }

    event.preventDefault();
    this.debouncedSetState.debounce(true);
  }

  handleMouseChange(event: React.MouseEvent) {
    if (!this.props.active || this.props.disableHover) {
      return;
    }
    this.debouncedSetState.debounce(event.type === 'mouseenter');
  }

  onFocusChange(eventType: EventType) {
    if (!this.props.active) {
      return;
    }
    this.debouncedSetState.debounce(eventType === EventType.Focus);
  }

  handleFocus(event: React.FocusEvent) {
    this.debouncedFocusChange.debounce(event.type);
  }

  handleBlur(event: React.FocusEvent) {
    this.debouncedFocusChange.debounce(event.type);
  }

  setContainerRef(ref: WithFirstTouchComponent<'div'>) {
    this.container = ref;
  }

  setInnerRef(innerRef: HTMLElement) {
    this.innerElem = innerRef;
  }

  handleTouchEnd(event: EventWithTarget<TouchEvent>) {
    if (this.state.focused && !this.innerElem.contains(event.target)) {
      this.setFocused(false);
    }
  }

  componentDidMount() {
    document.addEventListener('touchend', this.handleTouchEnd);
  }

  componentWillUnmount() {
    document.removeEventListener('touchend', this.handleTouchEnd);
    if (this.debouncedFocusChange) {
      this.debouncedFocusChange.cancel();
    }
    if (this.debouncedSetState) {
      this.debouncedSetState.cancel();
    }
  }

  render() {
    const { children, active, disableHover, onFocusLost, ...props } = this.props;
    const { focused } = this.state;

    return (
      <Container
        onFirstTouch={this.handleFirstTouch}
        onMouseEnter={this.handleMouseChange}
        onMouseLeave={this.handleMouseChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        ref={this.setContainerRef}
        innerRef={this.setInnerRef}
        data-testid="keep-focus-with-first-touch"
        {...props}
      >
        {children({ focused })}
      </Container>
    );
  }
}
